<script setup lang="ts">
/**
 * ElectionGrid Component - 100% API-Only Implementation
 *
 * Displays election contracts in both table and card views using live API data.
 * Supports filtering, sorting, and proper loading/error states.
 *
 * DATA ARCHITECTURE:
 * 1. Fetches data from tws.proxy/forecasttrader/election/grid API
 * 2. Transforms API response into candidate rows via apiTransformers.ts
 * 3. Creates contract structures for both table and card views
 * 4. No mock data dependencies - 100% real-time data
 *
 * VIEW MODES:
 * - Table: Hierarchical table with expandable candidate details
 * - Card: Individual election cards with candidate info
 *
 * Both views use raceId-based grouping via candidatesByRaceId
 *
 * FEATURES:
 * - Real-time API data with proper loading states
 * - Error handling with retry functionality
 * - Empty state messaging for no data scenarios
 * - Filtering and sorting controls
 * - Summary statistics from live data
 *
 * @see Plan: docs/plans/2026-02-12-feat-hook-up-market-data-bid-prices-plan.md
 */
import { computed, ref, inject, onBeforeUnmount, watch, nextTick, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery, useWindowScroll } from '@vueuse/core';
import { useQueries } from '@tanstack/vue-query';
import RangeSlider from './RangeSlider.vue';
import SelectButton from 'primevue/selectbutton';
import Button from 'primevue/button';
import Popover from 'primevue/popover';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import ElectionCard from './ElectionCard.vue';
import ElectionTableV2 from './ElectionTableV2.vue';
import { useElectionData, type SelectionInfo } from '../composables/useElectionData';
import { useElectionFeaturedProducts } from '../composables/useElectionFeaturedProducts';
import { mapFeaturedMarketsToGridProducts } from '../utils/balanceOfPowerTransformers';
import { useGetElectionGrid, useGetElectionStateGrid } from '@/api/forecasttrader.queries';
import { eventContractBidAskQueryOptions } from '@/api/mdfarm.queries';
import { transformApiResponseToTableRows, transformStateGridResponseToTableRows } from '../utils/apiTransformers';
import type { StateGroup } from '../utils/apiTransformers';
import { useUserState } from '@/stores/useUserState';
import { usePositions } from '@/stores/usePositions';
import type {
  ElectionContract,
  ElectionType,
  ViewMode,
  SortOption,
  CandidateTableRow,
  USStateCode,
} from '../types/election.types';
import type {
  ForecastTraderElectionGridParams,
  ForecastTraderElectionStateGridParams,
} from '@/types/forecasttrader.types';
import { HttpError } from '@/api/errors';
import { formatDollarPrice } from '@/utils/formatPrice';
import { useRealignOnViewportResize } from '@/composables/useRealignOnViewportResize';
import { allYesConids, allNoConids } from '../utils/matchedConids';

// Props
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    /** Current election type filter */
    electionType?: ElectionType;
    /** Current view mode */
    viewMode?: ViewMode;
    /** Optional state filter for focusing on specific state */
    stateFilter?: string;
    /** Race type selection (general, primary) with optional party */
    raceTypeParams?: {
      raceType: 'general' | 'primary';
      party?: 'Democrat' | 'Republican' | 'Nonpartisan';
    };
  }>(),
  {
    electionType: 'Senate', // Start with Senate as per plan
    viewMode: 'table', // Default to hierarchical table view
    stateFilter: undefined,
    raceTypeParams: () => ({ raceType: 'general' }), // Default to general elections
  },
);

// Emits
const emit = defineEmits<{
  /** Emitted when a contract/state is selected */
  selectState: [info: SelectionInfo];
  /** Emitted when a specific candidate is selected */
  selectCandidate: [info: SelectionInfo];
  /** Emitted when view mode changes */
  'update:viewMode': [mode: ViewMode];
  /** Emitted when share is requested for a candidate */
  share: [candidateConid: number];
}>();

// Map election type to API office parameter
function mapElectionTypeToOffice(electionType: ElectionType): 'governor' | 'senate' | 'house' {
  switch (electionType) {
    case 'Governor':
      return 'governor';
    case 'Senate':
      return 'senate';
    case 'House':
      return 'house';
    case 'All By State':
      // For "All By State", default to senate for now
      return 'senate';
    default:
      return 'senate';
  }
}

// Utility functions (must be defined before any reactive computations that use them)
// Map UI party selection to API party parameter
const getApiPartyName = (side: 'DEM' | 'GOP'): 'Democrat' | 'Republican' => {
  return side === 'DEM' ? 'Democrat' : 'Republican';
};

// Map UI price range (0-100) to API price params
// Omit min_price when 0 and max_price when 100 to avoid excluding contracts with no pricing
const mapPriceRangeToApi = (range: [number, number]): { min_price?: number; max_price?: number } => {
  const min = Math.min(range[0], range[1]);
  const max = Math.max(range[0], range[1]);

  const result: { min_price?: number; max_price?: number } = {};

  if (min > 0) {
    result.min_price = Math.max(1, Math.min(99, min));
  }
  if (max < 100) {
    result.max_price = Math.max(1, Math.min(99, max));
  }

  return result;
};

// Initialize composable early to access sortBy and mapSortToApiParams
// Use default empty array - we don't need client-side contract filtering anymore since API handles it
const { sortBy, priceRange, priceFilterSide, visibleTypes, toggleRaceType, mapSortToApiParams } = useElectionData();

// "Your Positions" filter for All By State mode
const showPositionsOnly = ref(false);

// User state for bid-ask query configuration
const userState = useUserState();

// Position data from centralized store (WebSocket-backed)
const positionsStore = usePositions();
const allCandidatePositionsMap = computed(() => positionsStore.positionDataMap);

// Per-row footer visibility: detect current grid column count from responsive breakpoints
const is2xl = useMediaQuery('(min-width: 1536px)');
const isXl = useMediaQuery('(min-width: 1280px)');
const isMd = useMediaQuery('(min-width: 600px)');
const isAboveXxs = useMediaQuery('(min-width: 400px)');

// Progressive condensing tiers for toolbar controls
// Tier 1 (xl+): sort segmented | filters button | Map/List
// Tier 2 (md&ndash;xl): sort icon toggle | filters pill | Map/List
// Tier 3 (<md): filter icon (sort in popover) | Map/List
const controlsTier = computed(() => {
  if (isXl.value) return 1;
  if (isMd.value) return 2;
  return 3;
});

const gridColumnCount = computed(() => {
  if (is2xl.value) return 4;
  if (isXl.value) return 3;
  if (isMd.value) return 2;
  return 1;
});

// Check if any candidate in a list has position data. A position may be held on
// a matched exchange (e.g. KALSHI) rather than the primary FORECASTX contract, so
// every yes/no conid across exchanges must be considered.
function candidatesHavePosition(candidates: CandidateTableRow[]): boolean {
  const positions = allCandidatePositionsMap.value;
  if (!positions || Object.keys(positions).length === 0) return false;
  for (const candidate of candidates) {
    if (allYesConids(candidate).some((conid) => conid && positions[conid])) return true;
    if (allNoConids(candidate).some((conid) => conid && positions[conid])) return true;
  }
  return false;
}

// Compute per-card showFooter based on row membership
const showFooterByIndex = computed(() => {
  const cols = gridColumnCount.value;
  const groups = cardRaceGroups.value;
  const result: boolean[] = new Array(groups.length).fill(false);

  for (let rowStart = 0; rowStart < groups.length; rowStart += cols) {
    const rowEnd = Math.min(rowStart + cols, groups.length);
    let rowHasPosition = false;
    for (let i = rowStart; i < rowEnd; i++) {
      if (candidatesHavePosition(groups[i].candidates)) {
        rowHasPosition = true;
        break;
      }
    }
    for (let i = rowStart; i < rowEnd; i++) {
      result[i] = rowHasPosition;
    }
  }
  return result;
});

// Committed price range — only updates when user releases the slider handle
const committedPriceRange = ref<[number, number]>([...priceRange.value] as [number, number]);

// Display value for the label — updates in realtime during drag without triggering parent reactivity
const sliderDisplayValue = ref<[number, number]>([...priceRange.value] as [number, number]);

function onSliderInput(val: [number, number]) {
  sliderDisplayValue.value = val;
}

function onSliderCommit(val: [number, number]) {
  priceRange.value = val;
  sliderDisplayValue.value = val;
  committedPriceRange.value = val;
}

// Whether the slider is at a non-default position (live, for instant label updates)
const isSliderTouched = computed(() => {
  return sliderDisplayValue.value[0] !== 0 || sliderDisplayValue.value[1] !== 100;
});

// Whether the committed price differs from default (for API calls)
const isSliderMoved = computed(() => {
  return committedPriceRange.value[0] !== 0 || committedPriceRange.value[1] !== 100;
});

// Whether the price filter is explicitly active:
// - Always active for primaries
// - Active when a party side (DEM/GOP) is explicitly selected
// - Active when ANY is selected but the slider has been moved from default
const isPriceFilterActive = computed(() => {
  if (props.raceTypeParams.raceType === 'primary' && props.raceTypeParams.party !== 'Nonpartisan') {
    return true;
  }
  if (priceFilterSide.value !== 'ANY') return true;
  return isSliderMoved.value;
});
// Price filter behavior based on race type
const isPrimaryElection = computed(() => props.raceTypeParams.raceType === 'primary');
const isPartisanPrimary = computed(() => isPrimaryElection.value && props.raceTypeParams.party !== 'Nonpartisan');

// Helper function to get effective price filter side for API
// Returns undefined when ANY is selected (no party-specific price filtering)
const getEffectivePriceFilterSide = (): 'DEM' | 'GOP' | undefined => {
  if (isPartisanPrimary.value) {
    return props.raceTypeParams.party === 'Democrat' ? 'DEM' : 'GOP';
  }
  return priceFilterSide.value === 'ANY' ? undefined : priceFilterSide.value;
};

// Is "All By State" mode
const isAllByState = computed(() => props.electionType === 'All By State');

// Election Grid API Query
const apiParams = computed<ForecastTraderElectionGridParams>(() => {
  const baseParams: ForecastTraderElectionGridParams = {
    office: mapElectionTypeToOffice(props.electionType),
    raceType: props.raceTypeParams.raceType,
    filters: {
      product: {
        ...(props.stateFilter ? { state: props.stateFilter } : {}),
      },
    },
    sorting: mapSortToApiParams(sortBy.value),
  };

  // Only send contract filter when explicitly active
  if (isPriceFilterActive.value) {
    const side = getEffectivePriceFilterSide();
    baseParams.filters!.contract = {
      ...mapPriceRangeToApi(committedPriceRange.value),
      ...(side ? { party: getApiPartyName(side) } : {}),
    };
  }

  // Add party filter for primary races
  if (props.raceTypeParams.raceType === 'primary' && props.raceTypeParams.party) {
    baseParams.party = props.raceTypeParams.party;
  }

  return baseParams;
});

// Fetch election data from API (disabled when "All By State" is active)
const {
  data: apiResponse,
  isLoading: gridLoading,
  error: gridError,
  isError: gridIsError,
  refetch: gridRefetch,
} = useGetElectionGrid(
  computed<ForecastTraderElectionGridParams | null>(() => {
    if (isAllByState.value) return null;
    return apiParams.value;
  }),
);

// FTAPI-231: Balance-of-power products (Federal Race Outcomes + the office-specific
// control product) are House/Senate only — no product exists for Governor or
// All-By-State. These render as ordinary top-of-list table rows (see `products`
// binding on ElectionTableV2 below), not a separate section.
const balanceOfPowerOffice = computed<'house' | 'senate' | undefined>(() => {
  if (isAllByState.value) return undefined;
  const office = apiParams.value.office;
  return office === 'house' || office === 'senate' ? office : undefined;
});
const { markets: balanceOfPowerMarkets } = useElectionFeaturedProducts(balanceOfPowerOffice);
const balanceOfPowerGridProducts = computed(() => mapFeaturedMarketsToGridProducts(balanceOfPowerMarkets.value));
// They have no election-race details to show (ElectionDetailsPanel expects real
// candidate data), so ElectionTableV2 links their rows out to Prediction Markets
// instead of opening it — see `linkedProductConids` below.
const balanceOfPowerProductConids = computed(() => balanceOfPowerGridProducts.value.map((p) => p.conid));

// === "All By State" State Grid API ===
const stateGridParams = computed<ForecastTraderElectionStateGridParams | null>(() => {
  if (!isAllByState.value) return null;

  // Default sends all 3 offices; when filters are active, send only selected ones
  // Sort for consistent query key ordering
  const allOffices = 'governor,house,senate';
  const offices =
    visibleTypes.value.size > 0
      ? [...visibleTypes.value]
          .map((t) => t.toLowerCase())
          .sort()
          .join(',')
      : allOffices;

  const params: ForecastTraderElectionStateGridParams = {
    offices,
    raceType: props.raceTypeParams.raceType,
    sorting: mapSortToApiParams(sortBy.value),
  };

  if (props.raceTypeParams.raceType === 'primary' && props.raceTypeParams.party) {
    params.party = props.raceTypeParams.party;
  }

  // Price filter
  if (isPriceFilterActive.value) {
    const side = getEffectivePriceFilterSide();
    params.filters = {
      contract: {
        ...mapPriceRangeToApi(committedPriceRange.value),
        ...(side ? { party: getApiPartyName(side) } : {}),
      },
    };
  }

  return params;
});

const {
  data: stateGridResponse,
  isLoading: stateGridLoading,
  error: stateGridError,
  isError: stateGridIsError,
  refetch: stateGridRefetch,
} = useGetElectionStateGrid(stateGridParams);

// Unified loading/error/refetch for both modes
const isLoading = computed(() => (isAllByState.value ? stateGridLoading.value : gridLoading.value));
const error = computed(() => (isAllByState.value ? stateGridError.value : gridError.value));
const isError = computed(() => (isAllByState.value ? stateGridIsError.value : gridIsError.value));
const refetch = () => (isAllByState.value ? stateGridRefetch() : gridRefetch());

// Transform state grid response to state groups
const allStateGroups = computed<StateGroup[]>(() => {
  if (!isAllByState.value || !stateGridResponse.value) return [];
  return transformStateGridResponseToTableRows(stateGridResponse.value);
});

// Filter state groups by position when "Your Positions" is active
const stateGroups = computed<StateGroup[]>(() => {
  if (!showPositionsOnly.value) return allStateGroups.value;
  const positions = allCandidatePositionsMap.value;
  if (!positions || Object.keys(positions).length === 0) return [];
  return allStateGroups.value
    .map((group) => ({
      ...group,
      rows: group.rows.filter(
        (row) =>
          allYesConids(row).some((conid) => positions[conid]) || allNoConids(row).some((conid) => positions[conid]),
      ),
    }))
    .filter((group) => group.rows.length > 0);
});

// Filtered state data for ElectionTableV2 — only states/products with positions when filter is active
const filteredStateData = computed(() => {
  if (!isAllByState.value || !stateGridResponse.value) return undefined;
  const states = stateGridResponse.value.states;
  if (!showPositionsOnly.value) return states;
  const positions = allCandidatePositionsMap.value;
  if (!positions || Object.keys(positions).length === 0) return [];
  return states
    .map((state) => ({
      ...state,
      products: state.products
        .map((product) => ({
          ...product,
          contracts: product.contracts.filter(
            (c) =>
              allYesConids(c).some((conid) => positions[conid]) || allNoConids(c).some((conid) => positions[conid]),
          ),
        }))
        .filter((product) => product.contracts.length > 0),
    }))
    .filter((state) => state.products.length > 0);
});

// Store individual candidate data for hierarchical table
const candidateTableRows = computed<CandidateTableRow[]>(() => {
  // In "All By State" mode, candidate rows come from stateGroups
  if (isAllByState.value) {
    return stateGroups.value.flatMap((g) => g.rows);
  }

  if (!apiResponse.value) return [];

  try {
    const tableRows = transformApiResponseToTableRows(apiResponse.value, apiParams.value.office);
    return tableRows;
  } catch {
    return [];
  }
});

// Note: contractsRef no longer needed since useElectionData doesn't require contracts
// All filtering/sorting is now handled server-side via API parameters

// Card view: one card per race, using the same raceId grouping as the list view.
// This replaces the old stateCode-raceType-district grouping to stay in sync.
const cardRaceGroups = computed(() => {
  return Array.from(candidatesByRaceId.value.entries()).map(([raceId, candidates]) => {
    const first = candidates[0];
    const contract: ElectionContract = {
      productConid: first.productConid,
      stateCode: first.stateCode,
      name: first.raceName,
      favorite: first.raceFavorite,
      price: first.racePrice || first.price,
      volume: first.volume?.toString() || '0',
      openInterest: first.openInterest || 0,
      district: first.district?.toString(),
      leadingDemConid: first.leadingDemConid,
      leadingRepConid: first.leadingRepConid,
    };
    return { raceId, contract, candidates };
  });
});

// Summary stats computed from cardRaceGroups — single pass instead of 3 inline .filter() calls
const summaryStats = computed(() => {
  let gop = 0;
  let dem = 0;
  let tossup = 0;
  cardRaceGroups.value.forEach(({ contract: c }) => {
    if (c.favorite === 'GOP') gop++;
    else if (c.favorite === 'DEM') dem++;
    if (!c.favorite) tossup++;
  });
  return { gop, dem, tossup };
});

const sliderModel = computed({
  get: () => priceRange.value,
  set: (val) => {
    priceRange.value = val as [number, number];
  },
});

// Override the price filter side when in primary mode
const effectivePriceFilterSide = computed({
  get: (): 'ANY' | 'DEM' | 'GOP' => {
    if (isPartisanPrimary.value) {
      return props.raceTypeParams.party === 'Democrat' ? 'DEM' : 'GOP';
    }
    return priceFilterSide.value;
  },
  set: (val: 'ANY' | 'DEM' | 'GOP') => {
    if (!isPartisanPrimary.value) {
      priceFilterSide.value = val;
    }
  },
});

// Slider color — gold when ANY, party-colored when active
const sliderColor = computed(() => {
  if (effectivePriceFilterSide.value === 'DEM') return 'var(--color-democrat)';
  if (effectivePriceFilterSide.value === 'GOP') return 'var(--color-republican)';
  return 'oklch(68.1% .162 75.834)';
});

// Price filter side options for SelectButton
type PriceFilterSide = 'ANY' | 'DEM' | 'GOP';

const priceFilterSideOptions: { label: string; value: PriceFilterSide }[] = [
  { label: 'ANY', value: 'ANY' },
  { label: 'DEM', value: 'DEM' },
  { label: 'REP', value: 'GOP' },
];

// Feature flag: show card/list view toggle
const showGridViewToggle = computed(() => app?.abFeatures?.hasFeature('SHOW_CARD_VIEW_TOGGLE'));

// View mode toggle options for card/table switch
const gridViewOptions = [
  { icon: 'ibi-list-items', value: 'table' as ViewMode, label: 'Table View' },
  { icon: 'ibi-grid-view', value: 'card' as ViewMode, label: 'Card View' },
];

// Sort options
const sortOptions: Array<{
  value: SortOption;
  label: string;
  icon: string;
  activeStyle: { backgroundColor: string; color: string };
}> = [
  {
    value: 'alphabetical',
    label: 'Alphabetical',
    icon: 'ibi-list-items',
    activeStyle: { backgroundColor: 'var(--color-election-brand)', color: 'var(--color-on-election-brand)' },
  },
  {
    value: 'open-interest',
    label: 'Popular',
    icon: 'ibi-star-rating',
    activeStyle: { backgroundColor: 'var(--color-election-brand)', color: 'var(--color-on-election-brand)' },
  },
];

// --- Candidate data for card view ---

// Group candidates by raceId for per-card lookup
const candidatesByRaceId = computed(() => {
  const map = new Map<string, CandidateTableRow[]>();
  candidateTableRows.value.forEach((row) => {
    if (!map.has(row.raceId)) map.set(row.raceId, []);
    map.get(row.raceId)!.push(row);
  });
  return map;
});

// --- Card visibility tracking for lazy loading ---
// Tracks which card indices are currently visible in the viewport
const visibleCardIndices = ref<Set<number>>(new Set());
const cardGridRef = ref<HTMLElement | null>(null);
let cardObserver: IntersectionObserver | null = null;

const hubStickyOffset = inject<Ref<number>>('stickyOffset', ref(0));

const stickyHeaderRef = ref<HTMLElement | null>(null);

// Margin loads cards slightly before they scroll into view
function setupCardObserver() {
  cleanupCardObserver();
  if (!cardGridRef.value) return;

  cardObserver = new IntersectionObserver(
    (entries) => {
      const updated = new Set(visibleCardIndices.value);
      entries.forEach((entry) => {
        const idx = Number((entry.target as HTMLElement).dataset.cardIdx);
        if (!Number.isNaN(idx)) {
          if (entry.isIntersecting) updated.add(idx);
          else updated.delete(idx);
        }
      });
      visibleCardIndices.value = updated;
    },
    { root: null, rootMargin: '200px' },
  );

  cardGridRef.value.querySelectorAll<HTMLElement>('[data-card-idx]').forEach((el) => {
    cardObserver!.observe(el);
  });
}

function cleanupCardObserver() {
  cardObserver?.disconnect();
  cardObserver = null;
}

onBeforeUnmount(cleanupCardObserver);

// Stable identity key for cardRaceGroups — changes only when the actual race list changes
// (different raceIds or different order), NOT on harmless reactive reference churn caused
// by parent re-renders (e.g., modal open/close, view toggle) creating new prop objects.
const cardRaceGroupsKey = computed(() => cardRaceGroups.value.map((g) => g.raceId).join(','));

// Re-observe whenever the race data actually changes (filter/sort/new API data)
watch(cardRaceGroupsKey, () => {
  cardVisibleConids.value = new Map(); // Clear stale conids from previous filter
  everSeenCandidateConids.value = new Set(); // Reset ever-seen set on new data
  allCandidateBidAskMap.value = {}; // Clear persistent bid-ask cache for new data
  nextTick(setupCardObserver);
});

// When switching TO card view, set up the card observer.
// When switching away, stop observing but preserve cached data (visibleCardIndices,
// cardVisibleConids, everSeenCandidateConids) so bid-ask prices persist across toggles.
watch(
  () => props.viewMode,
  (newMode) => {
    if (newMode === 'card') {
      nextTick(setupCardObserver);
    } else {
      cleanupCardObserver();
    }
  },
);

// Check if a specific card index is visible (with margin)
function isCardVisible(idx: number): boolean {
  return visibleCardIndices.value.has(idx);
}

// Collect conids reported as visible by each card's internal scroll observer
// Each card emits its visible-in-scroll conids; we aggregate them here
const cardVisibleConids = ref<Map<number, Set<number>>>(new Map());

function handleCardVisibleConids(cardIdx: number, conids: Set<number>) {
  const updated = new Map(cardVisibleConids.value);
  updated.set(cardIdx, conids);
  cardVisibleConids.value = updated;
}

// Ever-seen conid set: once a conid is observed visible, it stays in the query list.
// This prevents index-position misalignment when IntersectionObserver fires
// during layout shifts (e.g., modal open) and conids temporarily leave the viewport.
// With staleTime: Infinity, accumulated queries fetch once and stay cached.
const everSeenCandidateConids = ref<Set<number>>(new Set());

// Watch cardVisibleConids and ADD to the ever-seen set (never remove)
watch(
  cardVisibleConids,
  (conidMap) => {
    const current = everSeenCandidateConids.value;
    let grew = false;
    conidMap.forEach((conidSet) => {
      conidSet.forEach((conid) => {
        if (!current.has(conid)) grew = true;
      });
    });
    if (grew) {
      const updated = new Set(current);
      conidMap.forEach((conidSet) => {
        conidSet.forEach((conid) => updated.add(conid));
      });
      everSeenCandidateConids.value = updated;
    }
  },
  { deep: true },
);

// Stable conid list for queries — order never changes, only grows
const stableCandidateConids = computed(() => [...everSeenCandidateConids.value]);

// Conid → exchange lookup from all candidates
const conidExchangeMap = computed(() => {
  const map = new Map<number, string>();
  candidatesByRaceId.value.forEach((rows) => {
    rows.forEach((row) => {
      map.set(row.conid_yes, row.exchange);
      map.set(row.conid_no, row.exchange);
    });
  });
  return map;
});

// Bid-ask queries only for ever-seen candidates
const allCandidateBidAskQueries = useQueries({
  queries: computed(() =>
    stableCandidateConids.value.map((conid) => ({
      ...eventContractBidAskQueryOptions(
        conid,
        conidExchangeMap.value.get(conid) ?? 'FORECASTX',
        userState.usePublicApi,
      ),
      retry: false,
      refetchInterval: false as const,
      staleTime: Infinity,
    })),
  ),
});

// Persistent bid-ask map: only adds/updates entries, never removes them.
// This prevents data loss during reactive cycles where query.data is temporarily
// undefined (e.g., useQueries reshuffling, view toggle, modal overlay).
const allCandidateBidAskMap = ref<Record<number, { bid: number | null; ask: number | null }>>({});

watch(
  allCandidateBidAskQueries,
  (queries) => {
    const conids = stableCandidateConids.value;
    let changed = false;
    const current = allCandidateBidAskMap.value;

    queries.forEach((query, index) => {
      const conid = conids[index];
      if (conid && query.data) {
        const existing = current[conid];
        if (!existing || existing.bid !== query.data.bid || existing.ask !== query.data.ask) {
          changed = true;
        }
      }
    });

    if (changed) {
      const updated = { ...current };
      queries.forEach((query, index) => {
        const conid = conids[index];
        if (conid && query.data) {
          updated[conid] = { bid: query.data.bid, ask: query.data.ask };
        }
      });
      allCandidateBidAskMap.value = updated;
    }
  },
  { deep: true },
);

// Get election type for a contract
function getElectionType(contract: ElectionContract): 'Governor' | 'House' | 'Senate' {
  if (contract.district) return 'House';
  return props.electionType === 'Senate' ? 'Senate' : 'Governor';
}

// Handlers
function handleSelect(info: SelectionInfo) {
  emit('selectState', info);
}

// V2 table handlers — resolve productConid to SelectionInfo
// Products carry .state (full name); candidateTableRows carry .stateCode directly.
function resolveStateCode(productConid: number): USStateCode {
  const row = candidateTableRows.value.find((r) => r.productConid === productConid);
  if (row) return row.stateCode;
  console.warn(`[ElectionGrid] Could not resolve stateCode for productConid ${productConid}`);
  return 'AL' as USStateCode;
}

function handleV2RaceSelect(productConid: number) {
  emit('selectState', { stateCode: resolveStateCode(productConid), type: props.electionType, productConid });
}

function handleV2CandidateSelect({ productConid, conidYes }: { productConid: number; conidYes: number }) {
  emit('selectCandidate', {
    stateCode: resolveStateCode(productConid),
    type: props.electionType,
    productConid,
    candidateConid: conidYes,
  });
}

function handleSortChange(option: SortOption) {
  sortBy.value = option;
}

// Handle retry for failed API calls
function handleRetry() {
  refetch();
}

const { y: scrollY } = useWindowScroll();
const isScrolledDown = computed(() => scrollY.value > 200);

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// Compact collapsed controls
const pricePopoverRef = ref();
const sortPopoverRef = ref();
const stateFilterPopoverRef = ref();
const combinedPopoverRef = ref();

const { onShow: onPricePopoverShow, onHide: onPricePopoverHide } = useRealignOnViewportResize(() =>
  pricePopoverRef.value?.alignOverlay?.(),
);
const { onShow: onSortPopoverShow, onHide: onSortPopoverHide } = useRealignOnViewportResize(() =>
  sortPopoverRef.value?.alignOverlay?.(),
);
const combinedBtnRef = ref<HTMLElement | null>(null);

function togglePricePopover(event: Event) {
  pricePopoverRef.value?.toggle(event);
}

function openPricePopoverFromCombined() {
  combinedPopoverRef.value?.hide();
  setTimeout(() => {
    if (combinedBtnRef.value) {
      const rect = combinedBtnRef.value.getBoundingClientRect();
      const syntheticEvent = new PointerEvent('click', {
        bubbles: true,
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      });
      Object.defineProperty(syntheticEvent, 'currentTarget', { value: combinedBtnRef.value });
      pricePopoverRef.value?.toggle(syntheticEvent);
    }
  }, 200);
}

function toggleSortPopover(event: Event) {
  sortPopoverRef.value?.toggle(event);
}

function toggleStateFilterPopover(event: Event) {
  stateFilterPopoverRef.value?.toggle(event);
}

function toggleCombinedPopover(event: Event) {
  combinedPopoverRef.value?.toggle(event);
}

// PrimeVue's ConnectedOverlayScrollHandler only detects ancestors with explicit
// overflow:auto/scroll — it misses native viewport scrolling. Close popovers on scroll.
watch(scrollY, () => {
  pricePopoverRef.value?.hide();
  sortPopoverRef.value?.hide();
  stateFilterPopoverRef.value?.hide();
  combinedPopoverRef.value?.hide();
});

// All By State filter state
const isStateFilterActive = computed(() => visibleTypes.value.size > 0 || showPositionsOnly.value);
const stateFilterLabel = computed(() => {
  const parts: string[] = [];
  if (visibleTypes.value.size > 0) {
    parts.push(
      [...visibleTypes.value].map((t) => (t === 'Governor' ? 'Gov' : t === 'Senate' ? 'Sen' : 'House')).join(', '),
    );
  }
  if (showPositionsOnly.value) parts.push('Positions');
  return parts.length ? parts.join(' \u00b7 ') : 'Filters';
});

function resetStateFilters() {
  visibleTypes.value = new Set();
  showPositionsOnly.value = false;
}
</script>

<template>
  <div>
    <!-- Sticky filter bar: positioned below the hub sticky header -->
    <div
      ref="stickyHeaderRef"
      class="sticky !top-[calc(var(--padding-top,0px)+var(--hub-offset,0px))] z-10 bg-surface-low flex flex-col border-transparent transition-[border-color] duration-200"
      :style="{ '--hub-offset': `${hubStickyOffset}px` }"
    >
      <!-- Compact controls teleported to hub header \u2014 unified table controls container -->
      <Teleport v-if="!isAllByState" to="#grid-sort-controls" defer>
        <div
          :class="[
            'inline-flex items-center border border-[--p-content-border-color] bg-[--p-content-background] overflow-hidden shrink-0',
            controlsTier === 2 ? 'rounded-lg' : 'rounded-[10px]',
          ]"
        >
          <!-- Tier 3 (<md): Filter + Sort as compact buttons with popovers -->
          <template v-if="controlsTier === 3">
            <!-- Below 350px: combined filter+sort button -->
            <template v-if="!isAboveXxs">
              <button
                ref="combinedBtnRef"
                :class="[
                  'flex items-center gap-[5px] px-3 h-9 text-[13px] font-bold whitespace-nowrap cursor-pointer bg-transparent border-none transition-colors',
                  isSliderTouched || effectivePriceFilterSide !== 'ANY' || sortBy !== 'alphabetical'
                    ? 'text-election-brand'
                    : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
                ]"
                @click="toggleCombinedPopover"
                :title="$t('filterAndSort')"
              >
                <i class="ibi-filter text-sm" />
                <i class="ibi-list-items text-sm" />
              </button>
              <Popover ref="combinedPopoverRef">
                <div class="flex flex-col gap-3 p-1 min-w-[190px]">
                  <div class="flex flex-col gap-1">
                    <span class="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-color">{{
                      t('sort')
                    }}</span>
                    <button
                      v-for="opt in [
                        { value: 'alphabetical' as const, label: t('aToZ'), icon: 'ibi-list-items' },
                        { value: 'open-interest' as const, label: t('popular'), icon: 'ibi-star-rating' },
                      ]"
                      :key="opt.value"
                      class="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition-colors"
                      :class="
                        sortBy === opt.value
                          ? 'bg-election-brand text-on-election-brand'
                          : 'text-on-surface hover:bg-surface-hover hover:text-on-surface'
                      "
                      @click="sortBy = opt.value"
                    >
                      <i :class="opt.icon" class="text-sm" />
                      <span>{{ opt.label }}</span>
                    </button>
                  </div>
                  <div class="h-px bg-[--p-content-border-color]" />
                  <div class="flex flex-col gap-1">
                    <span class="px-2 text-[10px] font-bold uppercase tracking-wider text-muted-color">{{
                      t('filter')
                    }}</span>
                    <button
                      class="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition-colors text-on-surface hover:bg-surface-hover"
                      @click="openPricePopoverFromCombined"
                    >
                      <i class="ibi-filter text-sm" />
                      {{ $t('priceFilter') }}
                    </button>
                  </div>
                </div>
              </Popover>
            </template>

            <!-- 350px+: separate filter + sort buttons -->
            <template v-else>
              <button
                :class="[
                  'flex items-center gap-[5px] px-3 h-9 text-[13px] font-bold whitespace-nowrap cursor-pointer bg-transparent border-none [font-variant-numeric:tabular-nums] transition-colors',
                  isSliderTouched || effectivePriceFilterSide !== 'ANY'
                    ? 'text-election-brand'
                    : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
                ]"
                @click="togglePricePopover"
                :title="$t('filters')"
              >
                <i class="ibi-filter text-sm" />
              </button>

              <div class="w-px self-stretch bg-[--p-content-border-color]" />

              <button
                :class="[
                  'flex items-center gap-[5px] px-3 h-9 text-[13px] font-bold whitespace-nowrap cursor-pointer bg-transparent border-none [font-variant-numeric:tabular-nums] transition-colors',
                  sortBy !== 'alphabetical'
                    ? 'text-election-brand'
                    : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
                ]"
                @click="toggleSortPopover"
                :title="$t('sort')"
              >
                <i :class="sortBy === 'alphabetical' ? 'ibi-list-items' : 'ibi-star-rating'" class="text-sm" />
                <span class="hidden !visible min-[450px]:inline">{{ $t('sort') }}</span>
              </button>
              <Popover ref="sortPopoverRef" @show="onSortPopoverShow" @hide="onSortPopoverHide">
                <div class="flex flex-col gap-1 p-1 min-w-[140px]">
                  <button
                    v-for="opt in [
                      { value: 'alphabetical' as const, label: t('aToZ'), icon: 'ibi-list-items' },
                      { value: 'open-interest' as const, label: t('popular'), icon: 'ibi-star-rating' },
                    ]"
                    :key="opt.value"
                    class="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition-colors"
                    :class="
                      sortBy === opt.value
                        ? 'bg-election-brand text-on-election-brand'
                        : 'text-on-surface hover:bg-surface-hover hover:text-on-surface'
                    "
                    @click="
                      sortBy = opt.value;
                      sortPopoverRef?.hide();
                    "
                  >
                    <i :class="opt.icon" class="text-sm" />
                    {{ opt.label }}
                  </button>
                </div>
              </Popover>
            </template>
          </template>

          <!-- Tier 2+: Filter then Sort segmented inline -->
          <template v-else>
            <!-- Tier 2: compact filter pill (md&ndash;xl) -->
            <button
              v-if="controlsTier === 2"
              :class="[
                'flex items-center gap-[5px] px-2.5 h-8 text-xs font-bold whitespace-nowrap cursor-pointer bg-transparent border-none [font-variant-numeric:tabular-nums] transition-colors',
                isSliderTouched || effectivePriceFilterSide !== 'ANY'
                  ? effectivePriceFilterSide === 'DEM'
                    ? 'text-democrat'
                    : effectivePriceFilterSide === 'GOP'
                      ? 'text-republican'
                      : 'text-election-brand'
                  : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
              ]"
              @click="togglePricePopover"
            >
              <span
                v-if="effectivePriceFilterSide !== 'ANY'"
                class="w-2 h-2 rounded-sm shrink-0"
                :style="{ backgroundColor: sliderColor }"
              />
              <i class="ibi-filter text-xs" />
              <span class="inline-grid [&>*]:col-start-1 [&>*]:row-start-1">
                <span v-if="isSliderTouched" class="invisible" aria-hidden="true">$0.000&ndash;$0.000</span>
                <span v-if="isSliderTouched"
                  >${{ formatDollarPrice(sliderDisplayValue[0] / 100) }}&ndash;${{
                    formatDollarPrice(sliderDisplayValue[1] / 100)
                  }}</span
                >
                <span v-else>{{ t('filters') }}</span>
              </span>
              <i class="ibi-chevron-down text-[10px] opacity-50" />
            </button>

            <!-- Tier 1: full filter button (xl+) -->
            <button
              v-else
              :class="[
                'flex items-center gap-[5px] px-3.5 h-10 text-sm font-bold whitespace-nowrap cursor-pointer bg-transparent border-none [font-variant-numeric:tabular-nums] transition-colors',
                isSliderTouched || effectivePriceFilterSide !== 'ANY'
                  ? effectivePriceFilterSide === 'DEM'
                    ? 'text-democrat'
                    : effectivePriceFilterSide === 'GOP'
                      ? 'text-republican'
                      : 'text-election-brand'
                  : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
              ]"
              @click="togglePricePopover"
            >
              <i class="ibi-filter text-sm" />
              <span class="inline-grid [&>*]:col-start-1 [&>*]:row-start-1">
                <span v-if="isSliderTouched" class="invisible" aria-hidden="true">$0.000&ndash;$0.000</span>
                <span v-if="isSliderTouched"
                  >${{ formatDollarPrice(sliderDisplayValue[0] / 100) }}&ndash;${{
                    formatDollarPrice(sliderDisplayValue[1] / 100)
                  }}</span
                >
                <span v-else>{{ t('filters') }}</span>
              </span>
            </button>

            <div class="w-px self-stretch bg-[--p-content-border-color]" />

            <div class="inline-flex overflow-hidden shrink-0">
              <button
                v-for="opt in [
                  { value: 'alphabetical' as const, label: t('aToZ'), icon: 'ibi-list-items' },
                  {
                    value: 'open-interest' as const,
                    label: t('popular'),
                    icon: 'ibi-star-rating',
                  },
                ]"
                :key="opt.value"
                :class="[
                  'flex items-center gap-[5px] font-extrabold whitespace-nowrap cursor-pointer border-none transition-colors [&:not(:last-child)]:border-r [&:not(:last-child)]:border-[--p-content-border-color]',
                  controlsTier === 2 ? 'px-2.5 h-8 text-xs' : 'px-4 h-10 text-sm',
                  sortBy === opt.value
                    ? 'bg-election-brand text-on-election-brand'
                    : 'bg-transparent text-on-surface hover:bg-surface-hover hover:text-on-surface',
                ]"
                @click="sortBy = opt.value"
              >
                <i :class="[opt.icon, controlsTier === 2 ? 'text-xs' : 'text-sm']" />
                <span>{{ opt.label }}</span>
              </button>
            </div>
          </template>
        </div>
      </Teleport>

      <!-- Filter popover (needs separate teleport so it's not clipped by overflow) -->
      <Teleport v-if="!isAllByState" to="#grid-compact-controls" defer>
        <!-- Shared popover (works for both mobile + desktop triggers) -->
        <Popover ref="pricePopoverRef" @show="onPricePopoverShow" @hide="onPricePopoverHide">
          <div class="flex flex-col gap-2.5 w-[190px] p-0.5">
            <!-- Price filter controls -->
            <div class="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
              <template v-if="!isPartisanPrimary">
                <span class="text-xs font-bold uppercase tracking-wider text-on-surface">{{ t('party') }}</span>
                <div
                  class="inline-flex items-center overflow-hidden rounded-lg border border-[--p-content-border-color] divide-x divide-[--p-content-border-color] h-8"
                >
                  <button
                    v-for="opt in [
                      { label: 'ANY', value: 'ANY' as const },
                      { label: 'DEM', value: 'DEM' as const },
                      { label: 'REP', value: 'GOP' as const },
                    ]"
                    :key="opt.value"
                    class="flex flex-1 items-center justify-center gap-1 px-2 h-full text-[11px] font-bold cursor-pointer transition-colors"
                    :class="
                      effectivePriceFilterSide === opt.value
                        ? '!bg-election-brand !text-on-election-brand'
                        : 'text-muted-color hover:bg-surface-hover hover:text-on-surface'
                    "
                    @click="effectivePriceFilterSide = opt.value"
                  >
                    {{ opt.label }}
                  </button>
                </div>
              </template>
            </div>

            <div class="flex items-center gap-3">
              <span class="text-[10px] font-bold text-muted-color">$0</span>
              <RangeSlider
                v-model="sliderModel"
                :min="0"
                :max="100"
                :step="1"
                :color="sliderColor"
                class="w-full"
                @change="onSliderCommit"
                @input="onSliderInput"
              />
              <span class="text-[10px] font-bold text-muted-color">$1</span>
            </div>

            <div class="flex items-center justify-between">
              <span
                class="text-[13px] font-semibold text-on-surface [font-variant-numeric:tabular-nums]"
                :style="isSliderTouched ? { color: sliderColor } : {}"
              >
                {{
                  isSliderTouched
                    ? `$${formatDollarPrice(sliderDisplayValue[0] / 100)} &ndash; $${formatDollarPrice(sliderDisplayValue[1] / 100)}`
                    : 'All prices'
                }}
              </span>
              <button
                v-if="isSliderTouched || effectivePriceFilterSide !== 'ANY'"
                class="text-xs font-semibold text-primary cursor-pointer p-0 bg-transparent border-none hover:opacity-70"
                @click="
                  onSliderCommit([0, 100]);
                  effectivePriceFilterSide = 'ANY';
                "
              >
                {{ $t('reset') }}
              </button>
            </div>
          </div>
        </Popover>
      </Teleport>

      <!-- All By State: Filter controls teleported to nav bar -->
      <Teleport v-if="isAllByState" to="#grid-sort-controls" defer>
        <div
          class="inline-flex items-center rounded-[10px] border border-[--p-content-border-color] bg-[--p-content-background] overflow-hidden shrink-0"
        >
          <!-- Tier 3 (<md): filter icon only -->
          <button
            v-if="controlsTier === 3"
            :class="[
              'flex items-center gap-[5px] px-3 h-9 text-[13px] font-bold whitespace-nowrap cursor-pointer bg-transparent border-none [font-variant-numeric:tabular-nums] transition-colors',
              isStateFilterActive
                ? 'text-election-brand'
                : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
            ]"
            @click="toggleStateFilterPopover"
            :title="$t('filters')"
          >
            <i class="ibi-filter text-sm" />
          </button>

          <!-- Tier 2: compact filter pill -->
          <button
            v-else-if="controlsTier === 2"
            :class="[
              'inline-flex items-center gap-1 h-[38px] px-3 rounded-lg border border-[--p-content-border-color] bg-transparent text-sm font-bold whitespace-nowrap cursor-pointer shrink-0 [font-variant-numeric:tabular-nums] transition-colors',
              isStateFilterActive
                ? 'border-election-brand text-election-brand'
                : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
            ]"
            @click="toggleStateFilterPopover"
          >
            <i class="ibi-filter text-xs" />
            {{ stateFilterLabel }}
            <i class="ibi-chevron-down text-[10px] opacity-50" />
          </button>

          <!-- Tier 1 (xl+): full filter button -->
          <button
            v-else
            :class="[
              'flex items-center gap-[5px] px-3.5 h-10 text-sm font-bold whitespace-nowrap cursor-pointer bg-transparent border-none [font-variant-numeric:tabular-nums] transition-colors',
              isStateFilterActive
                ? 'text-election-brand'
                : 'text-on-surface hover:bg-surface-hover hover:text-on-surface',
            ]"
            @click="toggleStateFilterPopover"
          >
            <i class="ibi-filter text-sm" />
            {{ stateFilterLabel }}
          </button>
        </div>
      </Teleport>

      <!-- All By State: Filter popover (separate teleport to avoid clipping) -->
      <Teleport v-if="isAllByState" to="#grid-compact-controls" defer>
        <Popover ref="stateFilterPopoverRef">
          <div class="flex flex-col gap-3 p-1">
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-black uppercase tracking-wider text-on-surface">{{ $t('raceTypes') }}</span>
              <div class="flex gap-1">
                <button
                  v-for="type in [
                    { value: 'Governor' as const, label: $t('governor') },
                    { value: 'Senate' as const, label: $t('senate') },
                    { value: 'House' as const, label: $t('house') },
                  ]"
                  :key="type.value"
                  :class="[
                    'cursor-pointer rounded-lg border px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all',
                    visibleTypes.has(type.value)
                      ? 'border-election-brand bg-election-brand text-on-election-brand shadow-sm'
                      : 'border-[--p-content-border-color] bg-surface text-on-surface hover:bg-surface-hover hover:text-on-surface',
                  ]"
                  @click="toggleRaceType(type.value)"
                >
                  {{ type.label }}
                </button>
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-black uppercase tracking-wider text-on-surface">{{ $t('elections') }}</span>
              <div class="flex gap-1">
                <button
                  :class="[
                    'cursor-pointer rounded-lg border px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all',
                    !showPositionsOnly
                      ? 'border-election-brand bg-election-brand text-on-election-brand shadow-sm'
                      : 'border-[--p-content-border-color] bg-surface text-on-surface hover:bg-surface-hover hover:text-on-surface',
                  ]"
                  @click="showPositionsOnly = false"
                >
                  {{ $t('all') }}
                </button>
                <button
                  :class="[
                    'cursor-pointer rounded-lg border px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all',
                    showPositionsOnly
                      ? 'border-election-brand bg-election-brand text-on-election-brand shadow-sm'
                      : 'border-[--p-content-border-color] bg-surface text-on-surface hover:bg-surface-hover hover:text-on-surface',
                  ]"
                  @click="showPositionsOnly = true"
                >
                  {{ $t('yourPos') }}
                </button>
              </div>
            </div>
            <button
              v-if="isStateFilterActive"
              class="text-xs font-semibold text-primary cursor-pointer bg-transparent border-none p-0 text-left hover:opacity-70"
              @click="resetStateFilters"
            >
              Reset
            </button>
          </div>
        </Popover>
      </Teleport>

      <!-- Collapsible section: toggle + controls animate as a group -->
      <div
        class="grid grid-rows-[0fr] opacity-0 -translate-y-1.5 pointer-events-none transition-[opacity,transform] duration-[180ms] ease-in-out overflow-hidden"
      >
        <div class="flex flex-col gap-4 overflow-hidden">
          <!-- Card/List View Toggle -->
          <div v-if="showGridViewToggle && !isAllByState" class="flex justify-start md:justify-end">
            <SelectButton
              :model-value="viewMode"
              :options="gridViewOptions"
              option-value="value"
              :allow-empty="false"
              aria-label="Choose election view mode"
              :pt="{
                root: '!rounded-xl',
                pcToggleButton: { root: 'flex-1 sm:flex-initial !py-[0.5rem]' },
              }"
              @update:model-value="emit('update:viewMode', $event)"
            >
              <template #option="{ option }">
                <i :class="option.icon" class="text-base" :aria-label="option.label" :title="option.label" />
              </template>
            </SelectButton>
          </div>

          <!-- Sorting & Filtering Controls -->

          <!-- Mobile: inline compact filter row (below md) -->
          <div v-if="!isAllByState" class="!visible flex items-center gap-4 px-2 py-2 md:hidden">
            <div class="inline-flex overflow-hidden shrink-0 rounded-[10px] border border-[--p-content-border-color]">
              <button
                v-for="opt in [
                  { value: 'alphabetical' as const, label: t('aToZ'), icon: 'ibi-list-items' },
                  { value: 'open-interest' as const, label: t('oi'), icon: 'ibi-chart-pie-sector' },
                ]"
                :key="opt.value"
                :class="[
                  'flex items-center gap-[5px] px-2.5 h-8 text-xs font-extrabold whitespace-nowrap cursor-pointer border-none transition-colors [&:not(:last-child)]:border-r [&:not(:last-child)]:border-[--p-content-border-color]',
                  sortBy === opt.value
                    ? 'bg-election-brand text-on-election-brand'
                    : 'bg-transparent text-on-surface hover:bg-surface-hover hover:text-on-surface',
                ]"
                @click="sortBy = opt.value"
              >
                <i :class="opt.icon" class="text-xs" />
                <span>{{ opt.label }}</span>
              </button>
            </div>
            <SelectButton
              v-if="!isPartisanPrimary"
              v-model="effectivePriceFilterSide"
              :options="priceFilterSideOptions"
              option-label="label"
              option-value="value"
              :allow-empty="false"
              :pt="{
                root: '!border-[--p-content-border-color]',
                pcToggleButton: {
                  root: 'transition-all !py-1 !px-2 !border-[--p-content-border-color]',
                  label: 'text-[10px] !font-black',
                },
              }"
            />
            <div class="relative min-w-0 flex-1">
              <div
                class="absolute inset-x-0 -top-3 text-center text-[10px] font-black leading-none"
                :class="
                  !isSliderTouched ? 'text-muted-color' : effectivePriceFilterSide === 'ANY' ? 'text-on-surface' : ''
                "
                :style="effectivePriceFilterSide !== 'ANY' && isSliderTouched ? { color: sliderColor } : {}"
              >
                <template v-if="!isSliderTouched">{{ t('priceRange') }}</template>
                <template v-else
                  >${{ formatDollarPrice(sliderDisplayValue[0] / 100) }} - ${{
                    formatDollarPrice(sliderDisplayValue[1] / 100)
                  }}</template
                >
              </div>
              <div class="flex items-center gap-3">
                <span class="text-[10px] font-bold text-muted-color">$0</span>
                <RangeSlider
                  v-model="sliderModel"
                  :min="0"
                  :max="100"
                  :step="1"
                  :color="sliderColor"
                  class="w-full"
                  @change="onSliderCommit"
                  @input="onSliderInput"
                />
                <span class="text-[10px] font-bold text-muted-color">$1</span>
              </div>
            </div>
          </div>

          <!-- Desktop: full filter controls (md+) -->
          <div
            class="!visible overflow-hidden rounded-2xl bg-surface divide-[--p-content-border-color] border-[--p-content-border-color] hidden w-full flex-col items-stretch divide-y border shadow-sm md:flex md:flex-row md:items-center md:divide-x md:divide-y-0"
          >
            <div v-if="!isAllByState" class="flex flex-1 flex-col gap-2 p-4 sm:flex-row sm:items-center sm:gap-4">
              <div class="text-on-surface whitespace-nowrap text-xs font-black uppercase tracking-wider">
                Sort Races
              </div>
              <div class="inline-flex overflow-hidden shrink-0 rounded-[10px] border border-[--p-content-border-color]">
                <button
                  v-for="option in sortOptions"
                  :key="option.value"
                  :class="[
                    'flex items-center gap-[5px] px-3.5 h-[34px] text-[13px] font-extrabold whitespace-nowrap cursor-pointer border-none transition-colors [&:not(:last-child)]:border-r [&:not(:last-child)]:border-[--p-content-border-color]',
                    sortBy === option.value
                      ? 'bg-election-brand text-on-election-brand'
                      : 'bg-transparent text-on-surface hover:bg-surface-hover hover:text-on-surface',
                  ]"
                  @click="handleSortChange(option.value)"
                >
                  <i :class="option.icon" class="text-sm" />
                  {{ option.label }}
                </button>
              </div>
            </div>

            <!-- Price Range Slider -->
            <div
              v-if="!isAllByState"
              class="flex flex-1 flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-end sm:gap-6"
            >
              <div class="flex shrink-0 flex-col items-start justify-start gap-2">
                <div class="text-on-surface text-xs font-black uppercase tracking-wider leading-none">
                  {{ t('priceRange') }}
                </div>
                <SelectButton
                  v-if="!isPartisanPrimary"
                  v-model="effectivePriceFilterSide"
                  :options="priceFilterSideOptions"
                  option-label="label"
                  option-value="value"
                  :allow-empty="false"
                  :pt="{
                    root: '!border-[--p-content-border-color]',
                    pcToggleButton: {
                      root: 'transition-all !border-[--p-content-border-color]',
                      label: 'text-xs !font-black',
                    },
                  }"
                />
              </div>

              <div class="flex min-w-0 flex-1 flex-col">
                <div class="mb-4 flex items-center justify-between">
                  <div
                    class="text-xs font-black leading-none"
                    :class="
                      !isSliderTouched ? 'text-on-surface' : effectivePriceFilterSide === 'ANY' ? 'text-on-surface' : ''
                    "
                    :style="effectivePriceFilterSide !== 'ANY' && isSliderTouched ? { color: sliderColor } : {}"
                  >
                    <template v-if="!isSliderTouched">{{ t('allPrices') }}</template>
                    <template v-else
                      >${{ formatDollarPrice(sliderDisplayValue[0] / 100) }} - ${{
                        formatDollarPrice(sliderDisplayValue[1] / 100)
                      }}</template
                    >
                  </div>
                </div>
                <div class="flex items-center gap-3 px-1">
                  <span class="text-[10px] font-bold text-muted-color">$0</span>
                  <RangeSlider
                    v-model="sliderModel"
                    :min="0"
                    :max="100"
                    :step="1"
                    :color="sliderColor"
                    class="w-full"
                    @change="onSliderCommit"
                    @input="onSliderInput"
                  />
                  <span class="text-[10px] font-bold text-muted-color">$1</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div class="space-y-12">
      <!-- Loading State -->
      <div v-if="isLoading" class="py-20 text-center">
        <i class="pi pi-spin pi-spinner mx-auto mb-4 text-5xl text-on-surface" />
        <h3 class="text-lg font-black text-surface-900">{{ t('loadingElectionData') }}</h3>
        <p class="text-sm text-surface-500">{{ $t('fetchingLatestContracts') }}</p>
      </div>

      <!-- 404 State - No matching markets -->
      <div v-else-if="isError && error instanceof HttpError && error.status === 404" class="py-20 text-center">
        <i class="pi pi-exclamation-triangle mx-auto mb-4 text-5xl text-error" />
        <h3 class="text-lg font-black text-surface-900">
          {{ $t('noMatchingElectionMarkets') }}
        </h3>
      </div>

      <!-- Error State -->
      <div v-else-if="isError" class="py-20 text-center">
        <i class="pi pi-exclamation-triangle mx-auto mb-4 text-5xl text-error" />
        <h3 class="text-lg font-black text-surface-900">{{ t('errorLoadingData') }}</h3>
        <p class="text-sm text-surface-500 mb-4">
          {{ error instanceof Error ? error.message : $t('failedToLoadElectionData') }}
        </p>
        <Button @click="handleRetry" size="small" severity="secondary" class="text-xs">{{ $t('tryAgain') }}</Button>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="
          !isLoading &&
          (isAllByState
            ? !stateGridResponse || stateGridResponse.states.length === 0
            : !apiResponse || apiResponse.products.length === 0)
        "
        class="py-20 text-center"
      >
        <i class="ibi-money-profit-loss mx-auto mb-4 text-5xl text-on-surface" />
        <h3 class="text-lg font-black text-surface-900">{{ $t('noElectionDataAvailable') }}</h3>
        <p class="text-sm text-surface-500">{{ $t('noElectionRacesFound') }}</p>
      </div>

      <!-- Filtered Empty State (data exists but filtered out) -->
      <div v-else-if="!isLoading && cardRaceGroups.length === 0" class="py-20 text-center">
        <i class="ibi-money-profit-loss mx-auto mb-4 text-5xl text-on-surface" />
        <h3 class="text-lg font-black text-surface-900">{{ t('noContractsFound') }}</h3>
        <p class="text-sm text-surface-500">Try adjusting your filters to see more results.</p>
      </div>

      <!-- Data Views: v-show keeps both mounted to preserve state across toggles -->
      <template v-else>
        <!-- Election Table V2 — presentational, data via props -->
        <ElectionTableV2
          v-show="viewMode === 'table'"
          :products="
            isAllByState
              ? (filteredStateData?.flatMap((s) => s.products) ?? [])
              : [...balanceOfPowerGridProducts, ...(apiResponse?.products ?? [])]
          "
          :loading="isLoading"
          :office="isAllByState ? undefined : apiParams.office"
          :positions-map="allCandidatePositionsMap"
          :state-data="filteredStateData"
          :all-states-expanded="isAllByState && showPositionsOnly"
          :sort-by="sortBy"
          :linked-product-conids="balanceOfPowerProductConids"
          @select-race="handleV2RaceSelect"
          @select-candidate="handleV2CandidateSelect"
          @share="(conidYes) => emit('share', conidYes)"
        />

        <!-- Card Grid View - lazy loaded via IntersectionObserver -->
        <div
          v-show="viewMode === 'card'"
          ref="cardGridRef"
          class="grid grid-cols-1 gap-6 overflow-hidden md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          <div v-for="(group, idx) in cardRaceGroups" :key="group.raceId" :data-card-idx="idx">
            <ElectionCard
              :contract="group.contract"
              :candidates="group.candidates"
              :election-type="getElectionType(group.contract)"
              :index="idx"
              :bid-ask-map="allCandidateBidAskMap"
              :positions-map="allCandidatePositionsMap"
              :show-footer="showFooterByIndex[idx]"
              :visible="isCardVisible(idx)"
              @select="handleSelect"
              @select-candidate="(info) => emit('selectCandidate', info)"
              @update:visible-conids="handleCardVisibleConids(idx, $event)"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Sticky Summary Footer -->
    <div
      v-if="cardRaceGroups.length > 0"
      class="sticky bottom-[var(--padding-bottom,0)] z-[5] flex items-center justify-center gap-2 py-2.5 px-3 backdrop-blur-[12px] border-t border-[--p-content-border-color] shadow-[0_-2px_8px_rgb(0_0_0/4%)] sm:gap-4 sm:px-5 lg:py-3 lg:px-6 -mx-3 sm:-mx-8 lg:-mx-12 xl:-mx-16"
      :style="{ background: 'color-mix(in srgb, var(--p-surface) 85%, transparent)' }"
    >
      <div
        class="hidden sm:flex !visible flex-nowrap items-center justify-center gap-6 text-[0.8125rem] font-bold uppercase tracking-[0.05em] text-on-surface whitespace-nowrap min-w-0 lg:text-sm lg:gap-7"
      >
        <span>{{ cardRaceGroups.length }} {{ cardRaceGroups.length === 1 ? $t('contract') : $t('contracts') }}</span>
        <template v-if="!isPrimaryElection && priceFilterSide === 'ANY'">
          <span class="text-republican">{{ summaryStats.gop }} {{ t('gopLeading') }}</span>
          <span class="text-democrat">{{ summaryStats.dem }} {{ t('demLeading') }}</span>
          <span>{{ summaryStats.tossup }} {{ t('tossups') }}</span>
        </template>
      </div>
      <div
        class="flex sm:hidden !visible flex-nowrap items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.04em] text-on-surface whitespace-nowrap min-w-0"
      >
        <span>{{ $t('totalLabel') }} {{ cardRaceGroups.length }}</span>
        <template v-if="!isPrimaryElection && priceFilterSide === 'ANY'">
          <span class="text-republican">{{ $t('gopLabel') }} {{ summaryStats.gop }}</span>
          <span class="text-democrat">{{ $t('demLabel') }} {{ summaryStats.dem }}</span>
          <span>{{ $t('tossLabel') }} {{ summaryStats.tossup }}</span>
        </template>
      </div>
      <button
        v-if="isScrolledDown"
        class="inline-flex items-center gap-1 py-1 px-3 rounded-full border border-[--p-content-border-color] bg-[--p-content-background] text-on-surface text-xs font-bold uppercase tracking-[0.05em] cursor-pointer transition-all whitespace-nowrap hover:bg-surface hover:text-on-surface hover:border-surface lg:py-[5px] lg:px-3.5 lg:text-[0.8125rem]"
        @click="scrollToTop"
      >
        <i class="ibi-chevron-up" />
        Top
      </button>
    </div>
  </div>
</template>

<style scoped>
/* DataTable row group header - background and hover (requires :deep for PrimeVue internals) */
:deep(.election-table .p-datatable-row-group-header td[data-pc-section='rowgroupheadercell']) {
  background-color: var(--p-surface-50);
  transition: background-color 0.2s ease-in-out;
}

:deep(.election-table .p-datatable-row-group-header:hover td[data-pc-section='rowgroupheadercell']) {
  background-color: var(--p-surface-100);
}
</style>
<!-- last modified by AI tools on 2026-10-09 -->
